import createClient, { type Middleware } from "openapi-fetch";

import { ApiError, NetworkError } from "./errors";
import type { components, paths } from "./schema";

export type ClientKind = components["schemas"]["ClientDto"];
export type TokenResponse = components["schemas"]["TokenResponse"];

/**
 * Where tokens live. Web/admin keep the access token in memory only and rely
 * on the httpOnly refresh cookie; desktop stores the refresh token in the OS
 * keychain through the Tauri platform layer.
 */
export interface TokenStore {
  getAccessToken: () => string | null;
  setAccessToken: (token: string | null) => void;
  /** Desktop only. Web returns null (the cookie is invisible to JS). */
  getRefreshToken: () => Promise<string | null>;
  setRefreshToken: (token: string | null) => Promise<void>;
}

/** In-memory store for web/admin. */
export function memoryTokenStore(): TokenStore {
  let access: string | null = null;
  return {
    getAccessToken: () => access,
    setAccessToken: (t) => {
      access = t;
    },
    getRefreshToken: () => Promise.resolve(null),
    setRefreshToken: () => Promise.resolve(),
  };
}

export interface ApiClientOptions {
  baseUrl: string;
  client: ClientKind;
  tokens: TokenStore;
  /** Called when refresh fails and the user must sign in again. */
  onSessionExpired?: () => void;
  fetch?: typeof globalThis.fetch;
}

const CSRF_HEADER = "x-brandex-csrf";
const AUTH_PATHS_WITHOUT_RETRY = ["/v1/auth/login", "/v1/auth/refresh", "/v1/auth/register"];

/**
 * Creates the typed BrandEx API client.
 *
 * - Adds the bearer token to every request.
 * - On 401, performs one refresh (shared by concurrent callers) and retries.
 * - Throws {@link ApiError} for non-2xx responses so callers (TanStack Query)
 *   get typed failures.
 */
export function createApiClient(options: ApiClientOptions) {
  const doFetch = options.fetch ?? globalThis.fetch.bind(globalThis);
  let refreshing: Promise<boolean> | null = null;

  async function refresh(): Promise<boolean> {
    const refreshToken = await options.tokens.getRefreshToken();
    const response = await doFetch(`${options.baseUrl}/v1/auth/refresh`, {
      method: "POST",
      credentials: "include",
      headers: { "content-type": "application/json", [CSRF_HEADER]: "1" },
      body: JSON.stringify(
        refreshToken === null
          ? { client: options.client }
          : { client: options.client, refresh_token: refreshToken },
      ),
    });
    if (!response.ok) {
      options.tokens.setAccessToken(null);
      await options.tokens.setRefreshToken(null);
      return false;
    }
    const body = (await response.json()) as TokenResponse;
    await acceptTokens(body);
    return true;
  }

  async function acceptTokens(body: TokenResponse): Promise<void> {
    options.tokens.setAccessToken(body.access_token);
    if (body.refresh_token !== undefined && body.refresh_token !== null) {
      await options.tokens.setRefreshToken(body.refresh_token);
    }
  }

  /** Single-flight refresh: concurrent 401s share one refresh request. */
  function refreshOnce(): Promise<boolean> {
    refreshing ??= refresh()
      .catch(() => false)
      .finally(() => {
        refreshing = null;
      });
    return refreshing;
  }

  const retryable = new WeakMap<Request, Request>();

  const auth: Middleware = {
    onRequest({ request }) {
      const token = options.tokens.getAccessToken();
      if (token !== null) request.headers.set("authorization", `Bearer ${token}`);
      if (!AUTH_PATHS_WITHOUT_RETRY.some((p) => request.url.endsWith(p))) {
        retryable.set(request, request.clone());
      }
      return request;
    },
    async onResponse({ request, response }) {
      if (response.status === 401) {
        const error = await ApiError.fromResponse(response);
        // Only an invalid/expired session warrants refresh or sign-out. Other
        // 401s (e.g. `mfa_required`) are for the caller to handle.
        if (error.code !== "unauthenticated") throw error;
        const copy = retryable.get(request);
        if (copy !== undefined && (await refreshOnce())) {
          const token = options.tokens.getAccessToken();
          if (token !== null) copy.headers.set("authorization", `Bearer ${token}`);
          const retried = await doFetch(copy);
          if (retried.ok) return retried;
          const retryError = await ApiError.fromResponse(retried);
          if (retryError.code === "unauthenticated") options.onSessionExpired?.();
          throw retryError;
        }
        if (copy !== undefined) options.onSessionExpired?.();
        throw error;
      }
      if (!response.ok) throw await ApiError.fromResponse(response);
      return response;
    },
    onError({ error }) {
      if (error instanceof ApiError) return error;
      return new NetworkError(error);
    },
  };

  const http = createClient<paths>({
    baseUrl: options.baseUrl,
    credentials: "include",
    fetch: doFetch,
  });
  http.use(auth);

  return {
    http,
    /** Restores a session at startup (cookie or keychain). */
    restoreSession: refreshOnce,
    acceptTokens,
    async clearSession(): Promise<void> {
      options.tokens.setAccessToken(null);
      await options.tokens.setRefreshToken(null);
    },
    isAuthenticated: () => options.tokens.getAccessToken() !== null,
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;
